import { Component, Input, OnInit, inject, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { IconComponent } from "../icon/icon.component";
import { ListItemButtonComponent } from "./list-item-button/list-item-button.component";
import { RichTextEditorComponent } from "./rich-text-editor/rich-text-editor.component";
import { Script, Location, Character, Suggestion, ScriptService, Backdrop } from '../../service/script.service';
import { ActivatedRoute } from '@angular/router';

// Import dialog components
import { ScriptNameDialogComponent, ScriptNameDialogData, ScriptNameDialogResult } from './popups/script-name-dialog/script-name-dialog.component';
import { CategoriesDialogComponent, CategoriesDialogData, CategoriesDialogResult } from './popups/categories-dialog/categories-dialog.component';
import { BackdropDialogComponent, BackdropDialogData, BackdropDialogResult } from './popups/backdrop-dialog/backdrop-dialog.component';
import { CharacterDialogComponent, CharacterDialogData, CharacterDialogResult } from './popups/character-dialog/character-dialog.component';
import { SuggestionDialogComponent, SuggestionDialogData, SuggestionDialogResult } from './popups/suggestion-dialog/suggestion-dialog.component';
import { SoundDialogComponent, SoundDialogData, SoundDialogResult } from './popups/sound-dialog/sound-dialog.component';
import { DeleteScriptDialogComponent, DeleteScriptDialogData, DeleteScriptDialogResult } from './popups/delete-script-dialog/delete-script-dialog.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
  selector: 'app-script-editor',
  imports: [
    MatCardModule,
    MatDividerModule,
    MatListModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    ListItemButtonComponent,
    RichTextEditorComponent,
    IconComponent,
    MatSnackBarModule
],
  templateUrl: './script-editor.component.html',
  styleUrl: './script-editor.component.less'
})
export class ScriptEditorComponent implements OnInit {
  @Input() scriptId?: number;
  @Input() versionId?: number;
  @Input() showTutorial = false;
  @Input() readonly = false;
  @Input() isExample = false;
  @ViewChild(RichTextEditorComponent) richTextEditor!: RichTextEditorComponent;

  script!: Script;
  scriptContent: string = '';
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';

  scriptService = inject(ScriptService);
  dialog = inject(MatDialog);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  
  isBitSet(value: number, bit: number): boolean {
    return (value & (1 << bit)) !== 0;
  }

  isScriptRejected(script: Script): boolean {
    return (script.status & 4) !== 0;
  }

  openSoundDialog() {
    this.dialog.open(SoundDialogComponent, {
      width: '500px',
      data: { sounds: this.sounds } as SoundDialogData
    });
  }

  ngOnInit() {
    if (this.scriptId) {
      // Load existing script
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';
      
      this.scriptService.getScript(this.scriptId, this.versionId).subscribe({
        next: (script) => {
          this.script = script;
          this.scriptContent = script.content || '';
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load script:', error);
          this.isLoading = false;
          this.hasError = true;
          this.errorMessage = this.getUserFriendlyErrorMessage(error);
        }
      });
    } else if (this.isExample) {
        // Load hardcoded example script
        this.isLoading = false;
        this.hasError = false;
        
        this.script = {
            id: 0,
            title: 'Dryer Meeting',
            content: `Jazz music 1

BOSS: Alright, team! It's time to brainstorm the sequel to our hit product, "The Absolute CLOTHING Dryer."
BOSS: Remember, we need innovation, excitement, and of course, more unnecessary technology.
EMPLOYEE: Otherwise the shareholders will get upset?
BOSS: Precisely. And appeasing the shareholders is the most important thing in the universe, besides IMPORTANT_THING.

EMPLOYEE: (Raise hand) Boss, what if we create a "smart" CLOTHING drying machine with customizable LED lights?
BOSS: Hmm. Would we be able to re-use code from our failed project, the "smart" INVENTION?

EMPLOYEE: I guess so... I just like it when my phone talks to me all ADJECTIVEY-like.
EMPLOYEE: Like, (imitating phone voice) "Your socks are dry, Mister Handsome!"

BOSS: Ahem. Yes. We'll have to look into. (To intern) What about you, any ideas?

INTERN: What if we make "The Adventure CLOTHING"? It comes with a built-in GPS.
BOSS: What is this, some kind of joke?
INTERN: (Shy) No, I'm just trying to come up with ideas...

BOSS: (Handing INTERN a GPS device) Here's a GPS Device. Put that in your CLOTHING.
Meanwhile INTERN: (Receive the device and inspect it curiously)

Music Stops

BOSS: Do it. You won't. Because you can't.
BOSS: You're fired. 
BOSS: Actually, you're not fired. Do better. Be better.
INTERN: (Defeated) Yes, sir.`,
            categories: [], // Optional
            backdrop: {
                id: 1, // Assuming ID 1 exists, ideally should check available backdrops but hardcoding for example 
                name: 'Conference Room',
                img_url: '', // Frontend might need to resolve this or it might be fine empty if handled
                locations: []
            },
            characters: [
                { tag: 'BOSS', name: 'The Boss', location: { id: 1, name: 'Boss Chair', img_url: '' } },
                { tag: 'EMPLOYEE', name: 'A loyal employee', location: { id: 2, name: 'Office Chair 1', img_url: '' } },
                { tag: 'INTERN', name: 'The new intern', location: { id: 3, name: 'Office Chair 2', img_url: '' } }
            ],
            suggestions: [
                { tag: 'CLOTHING', description: 'Any article of clothing', examples: ['crispy sock', 'banana hammock', 'lobster blanket'] },
                { tag: 'IMPORTANT_THING', description: 'Something important', examples: ['seat belts', "Mother's Day", 'child labor laws'] },
                { tag: 'INVENTION', description: 'A machine that you wish existed', examples: ['candy for horses', 'NVIDIA GeForce RTX 9000', 'a car but chunkier'] },
                { tag: 'ADJECTIVEY', description: 'A personality-describing word', examples: ['easily spooked', 'upsetti spaghetti', 'stronk'] }
            ],
            author: { id: 0, name: 'System', avatar: '' },
            playerCount: 3,
            status: 0,
            isAnyVersionPublished: false
        };
        this.scriptContent = this.script.content;
    } else {
      // Create new empty script
      this.script = {
        id: 0,
        title: '',
        content: '',
        categories: [],
        backdrop: {
          id: 0,
          name: '',
          img_url: '',
          locations: []
        },
        characters: [],
        suggestions: [],
        author: {
          id: 0,
          name: '',
          avatar: ''
        },
        playerCount: 0,
        status: 0,
        isAnyVersionPublished: false
      };
      this.scriptContent = '';
      this.isLoading = false;
      this.hasError = false;
    }
  }

  retryLoadScript() {
    if (this.scriptId) {
      this.ngOnInit();
    }
  }

  goToScripts() {
    this.router.navigate(['/scripts']);
  }

  get sounds() {
    return this.scriptService.sounds;
  }

  get universal_locations() {
    return this.scriptService.universal_locations;
  }

  onScriptContentChange(content: string) {
    this.scriptContent = content;
    if (this.script) {
      this.script.content = content;
    }
    console.log('Script content updated:', content);
  }

  // Dialog methods
  openScriptNameDialog(): void {
    if (!this.script) return;
    
    const dialogRef = this.dialog.open(ScriptNameDialogComponent, {
      width: '500px',
      data: { title: this.script.title } as ScriptNameDialogData
    });

    dialogRef.afterClosed().subscribe((result: ScriptNameDialogResult) => {
      if (result && this.script) {
        this.script.title = result.title;
        // Trigger revalidation to update errors/warnings
        if (this.richTextEditor) {
          this.richTextEditor.forceTokenCalculation();
        }
      }
    });
  }


  openCategoriesDialog(): void {
    if (!this.script) return;
    
    const dialogRef = this.dialog.open(CategoriesDialogComponent, {
      width: '500px',
      data: { 
        categories: this.script.categories
      } as CategoriesDialogData
    });

    dialogRef.afterClosed().subscribe((result: CategoriesDialogResult) => {
      if (result && this.script) {
        this.script.categories = result.categories;
        // Trigger revalidation to update errors/warnings
        if (this.richTextEditor) {
          this.richTextEditor.forceTokenCalculation();
        }
      }
    });
  }

  openBackdropDialog(): void {
    if (!this.script) return;
    
    const dialogRef = this.dialog.open(BackdropDialogComponent, {
      width: '500px',
      data: { backdrop: this.script.backdrop } as BackdropDialogData
    });

    dialogRef.afterClosed().subscribe((result: BackdropDialogResult) => {
      if (result && this.script) {
        this.script.backdrop = result.backdrop;
        // Trigger revalidation to update errors/warnings
        if (this.richTextEditor) {
          this.richTextEditor.forceTokenCalculation();
        }
      }
    });
  }

  openCharacterDialog(character?: Character): void {
    if (!this.script) return;
    
    // Prevent adding new characters if limit is reached
    if (!character && (this.script.characters || []).length >= 6) {
      this.snackBar.open('Maximum of 6 characters allowed.', 'Close', { duration: 3000 });
      return;
    }
    
    const dialogRef = this.dialog.open(CharacterDialogComponent, {
      width: '500px',
      data: { 
        character: character,
        isEdit: !!character,
        backdrop: this.script.backdrop || null
      } as CharacterDialogData
    });

    dialogRef.afterClosed().subscribe((result: CharacterDialogResult) => {
      if (result && this.script) {
        if (result.delete && character) {
          // Delete existing character
          const index = this.script.characters.findIndex(c => c.tag === character.tag);
          if (index !== -1) {
            this.script.characters.splice(index, 1);
          }
        } else if (character) {
          // Edit existing character
          const index = this.script.characters.findIndex(c => c.tag === character.tag);
          if (index !== -1) {
            this.script.characters[index] = result.character;
          }
        } else {
          // Add new character (only if under limit)
          if ((this.script.characters || []).length < 6) {
            this.script.characters.push(result.character);
          } else {
            this.snackBar.open('Maximum of 6 characters allowed.', 'Close', { duration: 3000 });
            return;
          }
        }
        // Trigger revalidation to update errors/warnings
        if (this.richTextEditor) {
          this.richTextEditor.forceTokenCalculation();
        }
      }
    });
  }

  openSuggestionDialog(suggestion?: Suggestion): void {
    if (!this.script) return;
    
    // Prevent adding new suggestions if limit is reached
    if (!suggestion && (this.script.suggestions || []).length >= 12) {
      this.snackBar.open('Maximum of 12 suggestions allowed.', 'Close', { duration: 3000 });
      return;
    }
    
    const dialogRef = this.dialog.open(SuggestionDialogComponent, {
      width: '500px',
      data: { 
        suggestion: suggestion,
        isEdit: !!suggestion
      } as SuggestionDialogData
    });

    dialogRef.afterClosed().subscribe((result: SuggestionDialogResult) => {
      if (result && this.script) {
        if (result.delete && suggestion) {
          // Delete existing suggestion
          const index = this.script.suggestions.findIndex(s => s.tag === suggestion.tag);
          if (index !== -1) {
            this.script.suggestions.splice(index, 1);
          }
        } else if (suggestion) {
          // Edit existing suggestion
          const index = this.script.suggestions.findIndex(s => s.tag === suggestion.tag);
          if (index !== -1) {
            this.script.suggestions[index] = result.suggestion;
          }
        } else {
          // Add new suggestion (only if under limit)
          if ((this.script.suggestions || []).length < 12) {
            this.script.suggestions.push(result.suggestion);
          } else {
            this.snackBar.open('Maximum of 12 suggestions allowed.', 'Close', { duration: 3000 });
            return;
          }
        }
        // Trigger revalidation to update errors/warnings
        if (this.richTextEditor) {
          this.richTextEditor.forceTokenCalculation();
        }
      }
    });
  }

  private getUserFriendlyErrorMessage(error: any): string {
    // First, check if we have an API message (most specific)
    if (error.apiMessage) {
      return this.translateApiMessage(error.apiMessage);
    }
    
    // Handle HTTP status codes
    if (error.status === 404) {
      return 'This script does not exist or has been removed.';
    }
    if (error.status === 403) {
      return 'You do not have permission to access this script. You may not be the author or the script may be private.';
    }
    if (error.status === 401) {
      return 'Please log in to access this script.';
    }
    if (error.status === 400) {
      return 'Invalid script ID. Please check the URL and try again.';
    }
    if (error.status === 500) {
      return 'Server error. Please try again later or contact support if the problem persists.';
    }
    if (error.status === 0 || error.status === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    // Handle specific error messages from the API
    if (error.message && error.message.includes('Script not found')) {
      return 'This script does not exist or has been removed.';
    }
    if (error.message && error.message.includes("don't have permission")) {
      return 'You do not have permission to access this script. You may not be the author or the script may be private.';
    }
    if (error.message && error.message.includes('not available')) {
      return 'This script is not currently available for editing.';
    }
    
    // Default fallback message
    return 'Failed to load script. Please try again or contact support if the problem persists.';
  }

  private translateApiMessage(apiMessage: string): string {
    // Convert API messages to user-friendly versions
    const message = apiMessage.toLowerCase();
    
    if (message.includes('unauthorized')) {
      return 'Please log in to access this script.';
    }
    if (message.includes('forbidden') || message.includes('permission')) {
      return 'You do not have permission to access this script. You may not be the author or the script may be private.';
    }
    if (message.includes('not found') || message.includes('does not exist')) {
      return 'This script does not exist or has been removed.';
    }
    if (message.includes('invalid') || message.includes('bad request')) {
      return 'Invalid script ID. Please check the URL and try again.';
    }
    if (message.includes('server error') || message.includes('internal error')) {
      return 'Server error. Please try again later or contact support if the problem persists.';
    }
    
    // If no specific translation, return the original message but make it more user-friendly
    return apiMessage.charAt(0).toUpperCase() + apiMessage.slice(1) + '.';
  }

  /**
   * Update the script ID after successful submission
   * This ensures subsequent submissions update the existing script instead of creating a new one
   */
  updateScriptId(newId: number): void {
    if (this.script) {
      this.script.id = newId;
    }
  }

  /**
   * Get the current script data for submission
   * This method returns the complete script data that can be sent to the API
   */
  getScriptDataForSubmission(): any {
    if (!this.script) {
      return null;
    }

    return {
      id: this.script.id || 0,
      title: this.script.title || '',
      content: this.scriptContent || this.script.content || '',
      categories: this.script.categories || [],
      backdrop: this.script.backdrop || null,
      characters: this.script.characters || [],
      suggestions: this.script.suggestions || [],
      playerCount: this.script.playerCount || 0
    };
  }

  openDeleteScriptDialog(): void {
    if (!this.script) return;
    
    const dialogRef = this.dialog.open(DeleteScriptDialogComponent, {
      width: '500px',
      data: { 
        scriptTitle: this.script.title || 'Untitled Script'
      } as DeleteScriptDialogData
    });

    dialogRef.afterClosed().subscribe((result: DeleteScriptDialogResult) => {
      if (result && result.confirmed) {
        this.deleteScript();
      }
    });
  }

  private deleteScript(): void {
    if (!this.script || !this.script.id) return;
    
    this.scriptService.deleteScript(this.script.id).subscribe({
      next: (response: any) => {
        if (response.code === 200) {
          // Navigate back to scripts list after successful deletion
          this.snackBar.open('Script deleted successfully!');
          this.router.navigate(['/scripts']);
        } else {
          this.snackBar.open('Failed to delete script.', 'Close', { duration: 3000, panelClass: ['error-snackbar'] });
          console.error('Failed to delete script:', response);
          // You might want to show an error message here
        }
      },
      error: (error: any) => {
        console.error('Error deleting script:', error);
        this.snackBar.open('Failed to delete script.', 'Close', { duration: 3000, panelClass: ['error-snackbar'] });
        // You might want to show an error message here
      }
    });
  }


}
